/**
 * 被动累量看板
 */
import React, { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { Alert, Button, ButtonGroup } from '@tencent/spaui';
import { Loading } from '@tencent/ptp-fe-common';
import BasePage from '@/business-components/base-page';
import Toolbar from '@/common-components/toolbar';
import ListView from '@/business-components/list-view';
import useListView from '@/common/hooks/use-list-view';
import BreathingDot from '@/common-components/breathing-dot';
import Download from '@/common-components/Download';
import { isFreeLogin } from '@/services';
import globalStore from '@/stores';
import commonReport from '@/common/common-report';
import { getPassiveAccList, reportPassiveAccDownload } from './api';
import passiveAccumulationStore from './store';

import './index.scoped.less';

export enum FrameType {
  region = '区域',
  ecommerce = '电商',
}

const options = ({ payloads, customFunctions }: any) => {
  const { tableColumns, frameType } = payloads;
  const { columnRender } = customFunctions;

  return {
    fetchConfig: {
      action: getPassiveAccList,
      $onRequest: (params: any) => {
        const { batch_quarter, ...rest } = params;
        if (batch_quarter?.length) {
          batch_quarter.sort();
        }
        return {
          ...rest,
          frame_type: frameType,
          ...(batch_quarter?.length === 2 ? {
            batch_quarter_start: batch_quarter[0],
            batch_quarter_end: batch_quarter[1],
          } : {}),
          ...(batch_quarter?.length === 1 ? {
            batch_quarter_start: batch_quarter[0],
            batch_quarter_end: batch_quarter[0],
          } : {}),
        };
      },
    },
    tableConfig: {
      columns: tableColumns.map((v: any) => ({
        ...v,
        customRender: columnRender,
      })),
    },
  };
};

const PassiveAccumulationReport = () => {
  const { account } = globalStore;
  const { agentGroup, summaryInfoMap } = passiveAccumulationStore;
  const [frameType, setFrameType] = useState(FrameType.region);

  // 子页面切换上报
  useEffect(() => {
    if (account.dynamicNavAuth === null) {
      return;
    }
    commonReport.reportAction({
      action: 'page-view-sub',
      sub_module: frameType,
    });
  }, [account.dynamicNavAuth, frameType]);

  const {
    listRef,
    tableColumns,
    handleColumnsChange,
  } = useListView();

  const columnRender = useCallback((_type: string, _name: string, value: any) => {
    return value;
  }, []);

  // 下载成功回调
  const handleDownloadSuccess = useCallback(() => {
    if (!agentGroup || _.isEmpty(summaryInfoMap[frameType])) return;
    const { max_approve_time, max_batch_quarter } = summaryInfoMap[frameType];
    reportPassiveAccDownload({
      agent_group: agentGroup,
      frame_type: frameType,
      approve_time: max_approve_time,
      batch_quarter: max_batch_quarter,
    });
  }, [frameType, agentGroup, summaryInfoMap]);

  if (account.dynamicNavAuth === null) {
    return <Loading />;
  }

  return <BasePage
    bodyComponent={<div className="passive-accumulation-report">
      <Alert type="info" message='被动累量指无需经过销售提报，由AMS识别出关联而累量为同一客户政策集团，应用范围为渠道政策。请服务商告知涉及相关客户，如需申诉，请服务商按照要求的时间节点及时找行业销售发起。若错过申诉时间，将按照判定结果生效集团累量，直接影响服务商政策激励数额。行业销售最晚发起申诉时间为当季度末月25日；晚于25日的更新名单于次季度的首月12日之前完成申诉。请注意，若被动验证类别为"实际控股人相同+业务相似"或者“法人相同+业务相似”，不可申诉解除累量。' />
      <Toolbar
        left={<ButtonGroup
          theme="light"
          value={frameType}
          onChange={(_: any, value: FrameType) => setFrameType(value)}
        >
          <Button value={FrameType.region} data-report={`passive-accumulation-report-${FrameType.region}`}>
            <BreathingDot show={summaryInfoMap[FrameType.region]?.noticeDownload}>
              <span>区域类客户</span>
            </BreathingDot>
          </Button>
          <Button value={FrameType.ecommerce} data-report={`passive-accumulation-report-${FrameType.ecommerce}`}>
            <BreathingDot show={summaryInfoMap[FrameType.ecommerce]?.noticeDownload}>
              <span>电商类客户</span>
            </BreathingDot>
          </Button>
        </ButtonGroup>}
        right={[
          Toolbar.TOOL_TYPE.columns,
          (!isFreeLogin() ? <BreathingDot show={summaryInfoMap[frameType]?.noticeDownload} key="download">
            <Download
              key="download"
              type="passive_accumulation_report"
              param={{ frame_type: frameType }}
              onSuccess={handleDownloadSuccess}
              data-report={`download-passive_accumulation-${frameType}`}
            />
          </BreathingDot> : null),
        ]}
        columnsOptions={{
          useWuji: true,
          editable: false,
          module: 'passive_accumulation_report',
          updateSelectedColumns: handleColumnsChange,
          haveLabel: false,
        }}
      />
      <ListView
        listRef={listRef}
        options={options}
        columns={tableColumns}
        defaultFilterKeys={[
          'mdm_name',
          {
            key: 'group_name',
            title: '政策集团标签',
            config: {
              placeholder: '请输入政策集团标签',
            },
          },
          'accumulated_types',
          'approve_time',
          'batch_quarter',
        ]}
        payloads={{
          listRef,
          tableColumns,
          frameType,
        }}
        customFunctions={{
          columnRender,
        }}
      />
    </div>}
  />;
};

export default observer(PassiveAccumulationReport);
